import React, { useState, useRef, useEffect } from 'react';
import { Mail, CheckCircle2, AlertCircle, RefreshCw, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { verifyEmailOtp, resendEmailOtp } from '../services/supabaseAuth.ts';

interface EmailVerificationViewProps {
  email: string;
  password?: string;
  onVerified: (data: { account: any; session: any }) => void | Promise<void>;
  onChangeEmail: () => void;
}

export const EmailVerificationView: React.FC<EmailVerificationViewProps> = ({
  email,
  password,
  onVerified,
  onChangeEmail,
}) => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(
    `A 6-digit verification code has been sent to ${email}.`
  );

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Auto-focus first digit box on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  // Countdown timer for Resend OTP cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleOtpChange = (index: number, value: string) => {
    setErrorMsg(null);
    const digitsOnly = value.replace(/\D/g, '');
    if (!digitsOnly) {
      const updated = [...otp];
      updated[index] = '';
      setOtp(updated);
      return;
    }

    // Handle pasting 6 digits directly into a field
    if (digitsOnly.length === 6) {
      const pasted = digitsOnly.split('').slice(0, 6);
      setOtp(pasted);
      soundEngine.playClick();
      inputRefs[5].current?.focus();
      return;
    }

    // Single digit input with auto-advance
    const singleDigit = digitsOnly.slice(-1);
    const updated = [...otp];
    updated[index] = singleDigit;
    setOtp(updated);
    soundEngine.playClick();
    if (index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs[index - 1].current?.focus();
      } else if (otp[index]) {
        const updated = [...otp];
        updated[index] = '';
        setOtp(updated);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs[index - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      setErrorMsg(null);
      const split = pastedData.split('');
      const updated = [...otp];
      for (let i = 0; i < 6; i++) {
        updated[i] = split[i] || '';
      }
      setOtp(updated);
      soundEngine.playClick();
      const focusIndex = Math.min(5, split.length - 1);
      inputRefs[Math.max(0, focusIndex)].current?.focus();
    }
  };

  const fullOtpCode = otp.join('');

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (fullOtpCode.length !== 6 || isVerifying) {
      if (fullOtpCode.length !== 6) {
        setErrorMsg('Please enter all 6 digits of your verification code.');
      }
      return;
    }

    setIsVerifying(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const result = await verifyEmailOtp(email, fullOtpCode, password);
      console.log('[OTP Verify] verifyOtp result:', result);

      if (!result?.success || result?.error) {
        soundEngine.playWrong();
        setErrorMsg(
          result?.error ||
            'The verification code is invalid or has expired. Please check your email or click Resend Code.'
        );
        return;
      }

      const session = result?.session ?? null;
      console.log('[OTP Verify] Session found:', Boolean(session), session?.user?.email || email);

      soundEngine.playVictoryFanfare();
      setSuccessMsg('Email verified! Your account is active.');

      await onVerified({
        account: result?.account ?? null,
        session,
      });
    } catch (err: any) {
      console.error('[OTP Verify] Error in handleVerify:', err);
      soundEngine.playWrong();
      setErrorMsg(err?.message || 'OTP verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;

    setIsResending(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const result = await resendEmailOtp(email);
      if (!result.success) {
        setErrorMsg(result.error || 'Failed to resend code. Please try again later.');
      } else {
        soundEngine.playClick();
        setSuccessMsg(result.message || `A new 6-digit verification code was sent to ${email}.`);
        setCooldown(60);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error requesting new verification code.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-8 px-4 sm:px-6">
      <div className="rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-xl space-y-6 text-center">
        {/* Top Header Badge */}
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[#1C1C1C] text-[#A3A3A3] flex items-center justify-center border border-[#525252] shadow-xs">
          <Mail className="w-7 h-7" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-extrabold text-[#FAFAFA] tracking-tight flex items-center justify-center gap-2">
            <span>Verify Your Email</span>
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
          </h1>
          <p className="text-xs sm:text-sm text-[#D4D4D4] leading-relaxed max-w-sm mx-auto">
            We sent a 6-digit verification code to <span className="font-semibold text-[#FAFAFA] font-mono">{email}</span>. Please enter it below to activate your account.
          </p>
        </div>

        {/* Banners */}
        {errorMsg && (
          <div
            role="alert"
            className="p-3.5 rounded-2xl bg-red-500/10 border border-red-500/30 text-xs text-red-400 font-medium flex items-center gap-2 text-left"
          >
            <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div
            role="status"
            className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-400 font-medium flex items-center gap-2 text-left"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* 6-Digit OTP Box Input Form */}
        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={inputRefs[idx]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                data-testid={`otp-input-${idx}`}
                className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold font-mono rounded-xl border-2 border-[#262626] bg-[#1C1C1C] text-[#FAFAFA] focus:outline-none focus:border-[#525252] focus:ring-2 focus:ring-[#525252] transition-all"
                aria-label={`Digit ${idx + 1} of verification code`}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            <button
              type="submit"
              disabled={isVerifying || fullOtpCode.length !== 6}
              data-testid="verify-otp-button"
              className="w-full py-3 px-4 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-50 text-white font-semibold text-xs sm:text-sm transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer"
            >
              {isVerifying ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Verifying Code...</span>
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  <span>Verify Code</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-between gap-2 text-xs pt-1">
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || isResending}
                data-testid="resend-otp-button"
                className="inline-flex items-center gap-1.5 font-semibold text-[#A3A3A3] hover:underline disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
                <span>
                  {isResending
                    ? 'Sending...'
                    : cooldown > 0
                    ? `Resend Code in ${cooldown}s`
                    : 'Resend Code'}
                </span>
              </button>

              <button
                type="button"
                onClick={onChangeEmail}
                className="inline-flex items-center gap-1 font-medium text-[#A3A3A3] hover:text-[#FAFAFA] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Change Email</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
